/**
 * Symbol search box with autocomplete dropdown.
 *
 * Sits next to the EQUITIES pill row on /app/options. The always-on
 * pills are still rendered separately; this component adds a
 * search-driven path for the broader ~250-name curated universe.
 *
 * Behaviour:
 *   • Type → debounced call to /api/symbols/search → dropdown of
 *     matches with symbol + company name.
 *   • Empty box on focus → dropdown of "top entries" (the most-viewed
 *     indices / ETFs) so the user sees something useful immediately.
 *   • ↑/↓ navigates the dropdown; Enter picks the highlighted row;
 *     Esc closes it.
 *   • Click anywhere outside → closes the dropdown.
 *
 * The "no state cached until searched" rule is enforced on the
 * backend — picking a symbol just calls onPick(symbol). The
 * existing WebSocket pipeline takes over and triggers a lazy
 * pipeline run on the backend.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../../lib/api';

interface SymbolMatch {
  symbol: string;
  name: string;
}

interface SearchResponse {
  results: SymbolMatch[];
}

interface SymbolSearchProps {
  /** Called when the user picks a symbol (from dropdown click,
   *  enter, or manually typed + submitted). Always uppercase. */
  onPick: (symbol: string) => void;
}

const DEBOUNCE_MS = 120;

const SymbolSearch: React.FC<SymbolSearchProps> = ({ onPick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SymbolMatch[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced fetch — typing in flurries shouldn't fire a request
  // per keystroke. Backend search is cheap (in-process dict scan)
  // but the trip is still ~50-100 ms over the network.
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      apiGet<SearchResponse>(
        `/symbols/search?q=${encodeURIComponent(query)}&limit=10`,
        ctrl.signal,
      )
        .then(d => {
          setResults(d.results || []);
          setHighlighted(0);
        })
        .catch(() => {
          // Network errors are silent — the dropdown just shows
          // nothing. Users notice the missing options and try again.
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  // Close the dropdown when the user clicks anywhere outside us.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pickAndClose = useCallback(
    (symbol: string) => {
      const sym = symbol.trim().toUpperCase();
      if (!sym) return;
      onPick(sym);
      setQuery('');
      setOpen(false);
      // Drop focus so the dropdown doesn't immediately reopen on next
      // tab key; user can re-focus by clicking the box.
      inputRef.current?.blur();
    },
    [onPick],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted(h => Math.min(h + 1, Math.max(0, results.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Honour what the user TYPED. The dropdown selection is only used
      // when it's actually relevant to the typed text (its symbol starts
      // with what was typed). Otherwise `results` is stale — the debounced
      // search hasn't caught up to fast typing, so the highlighted row is
      // a leftover default (e.g. the empty-focus top entry, SPY). Picking
      // that instead of the typed ticker was the "defaults to SPY" bug.
      const typed = query.trim().toUpperCase();
      const hi = results[highlighted]?.symbol;
      const picked = typed
        ? (hi && hi.toUpperCase().startsWith(typed) ? hi : typed)
        : hi;
      if (picked) pickAndClose(picked);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const placeholder = useMemo(
    () => 'Search symbols (AAPL, NVDA, TLT, …)',
    [],
  );

  return (
    <div className="gv-symbol-search" ref={containerRef}>
      <span className="gv-symbol-search-icon" aria-hidden="true">⌕</span>
      <input
        ref={inputRef}
        type="search"
        autoComplete="off"
        spellCheck={false}
        className="gv-symbol-search-input"
        placeholder={placeholder}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Search symbols"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="gv-symbol-search-dropdown"
      />
      {open && results.length > 0 && (
        <ul
          id="gv-symbol-search-dropdown"
          className="gv-symbol-search-dropdown"
          role="listbox"
        >
          {results.map((r, i) => (
            <li
              key={r.symbol}
              role="option"
              aria-selected={i === highlighted}
              className={`gv-symbol-search-item${i === highlighted ? ' active' : ''}`}
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={(e) => {
                // mousedown (not click) so we beat the blur that would
                // close the dropdown before the click registers.
                e.preventDefault();
                pickAndClose(r.symbol);
              }}
            >
              <span className="gv-symbol-search-sym">{r.symbol}</span>
              <span className="gv-symbol-search-name">{r.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SymbolSearch;
